/* eslint-disable no-console */
import OAuthClient from 'client-oauth2'
import {
  CLIENT_ID,
  OAUTH_PROVIDER,
  OAUTH_PROVIDER_CODE,
  InfoResponse,
  ENABLE_SSO
} from '@src/endpoints/oauth'
import { AppStorage, Token as TokenType } from '@src/utils/storage.helpers'
import type { History } from 'history'
import jwt_decode from 'jwt-decode'
import uuid from 'uuid/v4'
import { Routes } from '@src/constants/routes'
import { storagePrefix } from '@src/constants/constants'
import type { HTTPMethod } from '@src/endpoints'
import { getQuery, getURITenant } from '@src/utils/url.utils'
import { isEmpty } from 'lodash'
const storage = new AppStorage(storagePrefix, sessionStorage)

interface Token {
  user_name?: string
}
interface SetTokensData {
  tokenData: OAuthClient.Token
  onSuccess?: (data: Token) => void
  onFailure?: () => void
}

export interface AuthServiceOptions {
  oauthOptions: InfoResponse
  dispatch?: any
  onSuccess?: (data: Token, callback: () => void) => void
}
type OAuth2Client = OAuthClient & {
  request?: (
    method: HTTPMethod,
    url: string,
    body?:
      | string
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | FormData
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | Document,
    headers?: string[]
  ) => void
}

class AuthService {
  private client: OAuth2Client = null
  public pending: {
    handleAuthentication: boolean
  }
  constructor(public options?: AuthServiceOptions) {
    this.pending = {
      handleAuthentication: false
    }
    this.client = new OAuthClient({
      clientId: CLIENT_ID,
      authorizationUri:
        OAUTH_PROVIDER || options?.oauthOptions?.authorizeEndpoint || '',
      accessTokenUri:
        OAUTH_PROVIDER_CODE || options?.oauthOptions?.tokenEndpoint || '',
      redirectUri: window.location.origin,
      state: this.getState(),
      query: getQuery()
    })
  }

  public setHandleAuthentication = (value: boolean) => {
    this.pending.handleAuthentication = value
  }

  private getState = () => {
    const urlParams = new URLSearchParams(window?.location?.search || '')
    const state = urlParams.get('state')
    return state ?? uuid()
  }
  public getLoginUrl = (query?: { [key: string]: string | string[] }) =>
    query ? this.client?.code.getUri({ query }) : this.client?.code.getUri()

  public getLogoutUrl = () => {
    const tenant = getURITenant()
    return `https://${tenant}.identitysoon.com/logout`
  }

  public logoutIdn = () => {
    storage.purge()
    return this.client.request('GET', this.getLogoutUrl(), '', [])
  }

  public refreshToken = async () => {
    const oauthToken = storage.get('oauthToken')
    const refreshToken = storage.get('refreshToken')
    if (!refreshToken) {
      return Promise.reject()
    }
    const token = this.client.createToken(
      oauthToken,
      refreshToken,
      'bearer',
      {}
    )
    const tokenData = await token.refresh()
    this.setTokens({ tokenData })
  }

  public redirect = () => {
    const oauthUrl = this.getLoginUrl(getQuery())
    if (oauthUrl) {
      return window.location.replace(oauthUrl)
    }
  }

  private setTokens = ({ tokenData, onSuccess, onFailure }: SetTokensData) => {
    try {
      const { refreshToken, accessToken } = tokenData
      storage.set('tokenType', 'oauthToken')
      if (refreshToken) {
        storage.set('refreshToken', refreshToken)
      }
      if (accessToken) {
        storage.set('oauthToken', accessToken)
        const dataToken: Token = jwt_decode(accessToken)
        if (typeof onSuccess === 'function') {
          onSuccess(dataToken)
        }
      }
    } catch (ex) {
      if (typeof onFailure === 'function') {
        onFailure()
      }
    }
  }
  public hasOauthToken = !isEmpty(storage.get('oauthToken'))

  public handleAuthentication = async (history: History) => {
    const urlParams = new URLSearchParams(window?.location?.search || '')
    const code = urlParams.get('code')
    if (
      this.pending.handleAuthentication ||
      !ENABLE_SSO ||
      !CLIENT_ID ||
      !code
    ) {
      return null
    }
    try {
      this.pending.handleAuthentication = true
      const tokenData = await this.client.code.getToken(window.location.href)
      this.setTokens({
        tokenData,
        onSuccess: data => {
          this.options.onSuccess(data, () => {
            this.pending.handleAuthentication = null
            if (this.isAuthenticated()) {
              history.push('/')
            }
          })
        },
        onFailure: () => {
          this.pending.handleAuthentication = null
          history.push({
            pathname: Routes.loginPage,
            search: window.location.search
          })
        }
      })
      this.pending.handleAuthentication = null
    } catch (error) {
      console.error(error)
      this.pending.handleAuthentication = null
    }
  }
  public getUserName = () => storage.get('userEmail')
  public getToken = () => this.client && storage.get('oauthToken')
  public isAuthenticated = () => !!this.getUserName() && !!this.getToken()

  public getTokenType = (): keyof TokenType => {
    const tokenType = storage.get('tokenType')
    if (!isEmpty(tokenType) && this.hasOauthToken) {
      return tokenType
    }
    if (this.hasOauthToken) {
      return 'oauthToken'
    }
  }
  get isOauthToken() {
    return this.getTokenType() === 'oauthToken'
  }
}

export default AuthService
